import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';
import { useEventSelection } from '../../contexts/useEventSelection';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ScheduleCardProps {
  isVisible: boolean;
  cardPosition: { x: number; y: number } | null;
  isDragging: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onClose: () => void;
  cardRef: React.RefObject<HTMLDivElement | null>;
}

export const ScheduleCard = ({
  isVisible,
  cardPosition,
  isDragging,
  onMouseDown,
  onClose,
  cardRef,
}: ScheduleCardProps) => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();

  if (!isVisible) return null;

  return (
    <div
      ref={cardRef}
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute',
        ...(cardPosition
          ? { left: cardPosition.x, top: cardPosition.y }
          : { top: 24, right: 70 }),
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        padding: '16px',
        borderRadius: 12,
        zIndex: isDragging ? 2000 : 1001,
        minWidth: 320,
        maxWidth: 400,
        border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(10px)',
        cursor: isDragging ? 'grabbing' : 'default',
      }}>
      {/* Header */}
      <div
        className="drag-handle"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
          cursor: 'grab',
          userSelect: 'none',
        }}>
        <div style={{
          fontSize: 16,
          fontWeight: 600,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        }}>
          Schedule
        </div>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
            minWidth: 24,
            padding: '0 4px',
          }}
        />
      </div>

      {/* Content */}
      <div style={{
        fontSize: 13,
        lineHeight: 1.6,
        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
      }}>
        <div style={{
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 12,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        }}>
          Radar Operational Hours
        </div>

        {/* Radar Periods */}
        {selectedEvent?.radar_periods && selectedEvent.radar_periods.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {selectedEvent.radar_periods.map((period, index) => {
              const timezone = selectedEvent.circuit?.time_zone || 'UTC';
              // Parse timestamps - handle various formats from BigQuery
              const parseTimestamp = (ts: string) => {
                // Remove " UTC" suffix if present
                const cleaned = ts.replace(/ UTC$/, '').trim();
                return dayjs.utc(cleaned).tz(timezone);
              };

              const startTime = parseTimestamp(period.start_time);
              const endTime = parseTimestamp(period.end_time);

              // Check if dates are valid
              if (!startTime.isValid() || !endTime.isValid()) {
                console.error('Invalid radar period timestamps:', period);
                return null;
              }

              const isSameDay = startTime.format('YYYY-MM-DD') === endTime.format('YYYY-MM-DD');

              return (
                <div
                  key={index}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    borderRadius: 8,
                    border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                  }}
                >
                  <div style={{
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 4,
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
                  }}>
                    {startTime.format('ddd DD MMM')}
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.65)',
                  }}>
                    {isSameDay ? (
                      <>
                        {startTime.format('HH:mm')} - {endTime.format('HH:mm')}
                      </>
                    ) : (
                      <>
                        {startTime.format('HH:mm')} ({startTime.format('ddd DD')}) - {endTime.format('HH:mm')} ({endTime.format('ddd DD')})
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{
            padding: '12px',
            textAlign: 'center',
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
            fontSize: 12,
          }}>
            No radar periods available
          </div>
        )}
      </div>
    </div>
  );
};
